import type { FC, ReactElement, ReactNode } from 'react';

import { MobileNavItem } from './mobile-nav-item';

interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
  tooltip?: string;
  showTooltip?: boolean;
}

const renderItems = ({
  depth = 0,
  items,
  pathname,
}: {
  depth?: number;
  items: Item[];
  pathname?: string | null;
}): ReactElement[] =>
  items.reduce(
    (acc: ReactElement[], item) =>
      reduceChildRoutes({
        acc,
        depth,
        item,
        pathname,
      }),
    [],
  );

const reduceChildRoutes = ({
  acc,
  depth,
  item,
  pathname,
}: {
  acc: ReactElement[];
  depth: number;
  item: Item;
  pathname?: string | null;
}): Array<ReactElement> => {
  const checkPath = !!(item.path && pathname);
  const partialMatch = checkPath ? pathname.includes(item.path!) : false;
  const exactMatch = checkPath ? pathname === item.path : false;

  if (item.items) {
    acc.push(
      <MobileNavItem
        active={partialMatch}
        depth={depth}
        disabled={item.disabled}
        icon={item.icon}
        key={item.title}
        label={item.label}
        open={partialMatch}
        title={item.title}
      >
        <ul className="tw:flex tw:flex-col tw:gap-1 tw:list-none tw:m-0 tw:p-0">
          {renderItems({
            depth: depth + 1,
            items: item.items,
            pathname,
          })}
        </ul>
      </MobileNavItem>,
    );
  } else {
    acc.push(
      <MobileNavItem
        active={exactMatch}
        depth={depth}
        disabled={item.disabled}
        external={item.external}
        icon={item.icon}
        key={item.title}
        label={item.label}
        path={item.path}
        title={item.title}
        tooltip={item.tooltip}
        showTooltip={item.showTooltip}
      />,
    );
  }

  return acc;
};

interface MobileNavSectionProps {
  items?: Item[];
  pathname?: string | null;
  subheader?: string;
}

export const MobileNavSection: FC<MobileNavSectionProps> = (props) => {
  const { items = [], pathname, subheader = '', ...other } = props;

  return (
    <ul className="tw:flex tw:flex-col tw:gap-1 tw:list-none tw:m-0 tw:p-0" {...other}>
      {subheader && (
        <li
          style={{
            color: 'var(--nav-section-title-color)',
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.66,
            marginBottom: 4,
            marginLeft: 4,
            textTransform: 'uppercase',
          }}
        >
          {subheader}
        </li>
      )}
      {renderItems({ items, pathname })}
    </ul>
  );
};
